import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react/react/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import data from '../../src/Token/Token.docs.json';
import { AvatarToken, IssueLabelToken, Token } from '@primer/react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const InlineCode = makeShortcode("InlineCode");
const ComponentProps = makeShortcode("ComponentProps");
const ComponentChecklist = makeShortcode("ComponentChecklist");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`The default `}<inlineCode parentName="p">{`Token`}</inlineCode>{` can be used for most cases, but specialized token components are provided for specific use cases (see below for more info).`}</p>
    <p>{`All types of tokens may have the same interactive qualities as links or buttons. They also have a sense of being "selected" and ready for user interaction such as being removed from a collection.`}</p>
    <h2>{`Examples`}</h2>
    <h3>{`Basic`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<>
  <Text fontSize={0} display="block" color="fg.subtle">
    Token
  </Text>
  <Token text="Default Token" />
</>
`}</code></pre>
    <h3>{`Kitchen sink`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  display="flex"
  sx={{
    alignItems: 'start',
    flexDirection: 'column',
    gap: 6,
  }}
>
  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Resting
    </Text>
    <Token text="Default Token" />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      isSelected
    </Text>
    <Token text="Default Token" isSelected />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      With \`leadingVisual\` passed
    </Text>
    <Token text="Default Token" leadingVisual={GitBranchIcon} />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      With \`onRemove\` passed
    </Text>
    <Token
      text="Default Token"
      onRemove={() => {
        console.log('remove me')
      }}
    />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Size options
    </Text>
    <Box
      display="flex"
      sx={{
        alignItems: 'start',
        gap: 2,
      }}
    >
      <Token size="small" text="'small' Token" />
      <Token size="medium" text="'medium' Token" />
      <Token size="large" text="'large' Token (default)" />
      <Token size="xlarge" text="'xlarge' Token" />
    </Box>
  </div>
</Box>
`}</code></pre>
    <h3>{`With leadingVisual`}</h3>
    <Note variant="warning" mdxType="Note">
  A <InlineCode mdxType="InlineCode">leadingVisual</InlineCode> should not be used with the <InlineCode mdxType="InlineCode">small</InlineCode> size option if
  you're rendering an icon from Octicons
    </Note>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  display="flex"
  sx={{
    alignItems: 'start',
    flexDirection: 'column',
    gap: 6,
  }}
>
  <Token size="medium" text="'medium' Token" leadingVisual={GitBranchIcon} />
  <Token size="large" text="'large' Token (default)" leadingVisual={GitBranchIcon} />
  <Token size="xlarge" text="'xlarge' Token" leadingVisual={GitBranchIcon} />
</Box>
`}</code></pre>
    <h3>{`Interactive tokens`}</h3>
    <p>{`If any token is interactive (it is a link or a button), it will become focusable, and react to being hovered or focused on.`}</p>
    <p>{`If a token is interactive `}<em parentName="p">{`and`}</em>{` has a remove button (appears when passing `}<inlineCode parentName="p">{`onRemove`}</inlineCode>{`), the remove button should not get focus. To invoke the `}<inlineCode parentName="p">{`onRemove`}</inlineCode>{` callback without using the cursor, press 'Backspace' when the token is focused.`}</p>
    <p>{`These rules apply to all token components mentioned in this document.`}</p>
    <h4>{`Interactive tokens with and without an `}<inlineCode parentName="h4">{`onRemove`}</inlineCode>{` prop`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  display="flex"
  sx={{
    alignItems: 'start',
    flexDirection: 'column',
    gap: 6,
  }}
>
  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Interactive
    </Text>
    <Box
      display="flex"
      sx={{
        alignItems: 'start',
        gap: 2,
      }}
    >
      <Token as="a" href="http://google.com/" text="Link" />
      <Token as="button" onClick={() => console.log('clicked')} text="Button" />
      <Token as="span" tabIndex={0} onFocus={() => console.log('focused')} text="Focusable Span" />
    </Box>
  </div>
  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Interactive with \`onRemove\` passed
    </Text>
    <Box
      display="flex"
      sx={{
        alignItems: 'start',
        gap: 2,
      }}
    >
      <Token
        as="a"
        href="http://google.com/"
        text="Link"
        onRemove={() => {
          console.log('remove me')
        }}
      />
      <Token
        as="button"
        onClick={() => console.log('clicked')}
        text="Button"
        onRemove={() => {
          console.log('remove me')
        }}
      />
      <Token
        as="span"
        tabIndex={0}
        onFocus={() => console.log('focused')}
        text="Focusable Span"
        onRemove={() => {
          console.log('remove me')
        }}
      />
    </Box>
  </div>
</Box>
`}</code></pre>
    <h3>{`IssueLabelToken`}</h3>
    <p>{`Tokens that represent Issue labels should use the `}<inlineCode parentName="p">{`IssueLabelToken`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  display="flex"
  sx={{
    alignItems: 'start',
    flexDirection: 'column',
    gap: 6,
  }}
>
  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Resting
    </Text>
    <IssueLabelToken text="good first issue" fillColor="#0366d6" />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      isSelected
    </Text>
    <IssueLabelToken text="good first issue" fillColor="#0366d6" isSelected />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      With \`onRemove\` passed
    </Text>
    <IssueLabelToken
      text="good first issue"
      fillColor="#0366d6"
      onRemove={() => {
        console.log('remove me')
      }}
    />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Size options
    </Text>
    <Box
      display="flex"
      sx={{
        alignItems: 'start',
        gap: 2,
      }}
    >
      <IssueLabelToken
        fillColor="#0366d6"
        size="small"
        text="'small' Token"
        onRemove={() => {
          console.log('remove me')
        }}
      />
      <IssueLabelToken
        fillColor="#0366d6"
        size="medium"
        text="'medium' Token"
        onRemove={() => {
          console.log('remove me')
        }}
      />
      <IssueLabelToken
        fillColor="#0366d6"
        size="large"
        text="'large' Token (default)"
        onRemove={() => {
          console.log('remove me')
        }}
      />
      <IssueLabelToken
        fillColor="#0366d6"
        size="xlarge"
        text="'xlarge' Token"
        onRemove={() => {
          console.log('remove me')
        }}
      />
    </Box>
  </div>
</Box>
`}</code></pre>
    <h3>{`AvatarToken`}</h3>
    <p>{`Tokens that represent GitHub users should use the `}<inlineCode parentName="p">{`AvatarToken`}</inlineCode>{` component.`}</p>
    <Note variant="warning" mdxType="Note">
  This component should not be used with the <InlineCode mdxType="InlineCode">small</InlineCode> or <InlineCode mdxType="InlineCode">medium</InlineCode> size
  option
    </Note>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  display="flex"
  sx={{
    alignItems: 'start',
    flexDirection: 'column',
    gap: 6,
  }}
>
  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Resting
    </Text>
    <AvatarToken avatarSrc="https://avatars.githubusercontent.com/mperrotti" text="Default Token" />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      isSelected
    </Text>
    <AvatarToken avatarSrc="https://avatars.githubusercontent.com/mperrotti" text="Default Token" isSelected />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      With \`onRemove\` passed
    </Text>
    <AvatarToken
      avatarSrc="https://avatars.githubusercontent.com/mperrotti"
      text="Default Token"
      onRemove={() => {
        console.log('remove me')
      }}
    />
  </div>

  <div>
    <Text fontSize={0} display="block" color="fg.subtle">
      Size options
    </Text>
    <Box
      display="flex"
      sx={{
        alignItems: 'start',
        gap: 2,
      }}
    >
      <AvatarToken
        avatarSrc="https://avatars.githubusercontent.com/mperrotti"
        size="large"
        text="'large' Token (default)"
        onRemove={() => {
          console.log('remove me')
        }}
      />
      <AvatarToken
        avatarSrc="https://avatars.githubusercontent.com/mperrotti"
        size="xlarge"
        text="'xlarge' Token"
        onRemove={() => {
          console.log('remove me')
        }}
      />
    </Box>
  </div>
</Box>
`}</code></pre>
    <h2>{`Props`}</h2>
    <ComponentProps data={data} mdxType="ComponentProps" />
    <h2>{`Status`}</h2>
    <ComponentChecklist items={{
      propsDocumented: true,
      noUnnecessaryDeps: true,
      adaptsToThemes: true,
      adaptsToScreenSizes: true,
      fullTestCoverage: true,
      usedInProduction: false,
      usageExamplesDocumented: true,
      designReviewed: false,
      a11yReviewed: false,
      stableApi: false,
      addressedApiFeedback: false,
      hasDesignGuidelines: true,
      hasFigmaComponent: false
    }} mdxType="ComponentChecklist" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      